import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AutoComplete, Button, Input, Select, Spin } from 'antd';
import {
  getOrderGenieErpCustomerById,
  listOrderGenieErpContacts,
  lookupOrderGenieCustomersHybrid,
  lookupOrderGenieErpCustomerByEntityId,
  lookupOrderGenieShipToHybrid,
} from '../../services/orderXApi';
import { formatCustomerLabel, formatShipToLabel } from '../../pages/orderXV4Utils';

// Small provenance tag for a merged ERP + OrderX (OpenSearch) candidate row.
// 'verified' = ERP and the OrderX index agree on this customer right now;
// 'erp' = in the ERP, not indexed yet; 'opensearch' = indexed, not
// confirmed against ERP this request (e.g. connector has no free-text
// search, or ERP timed out).
const CUSTOMER_SOURCE_CHIP_STYLE = {
  verified: { label: 'Verified', background: '#e7f4ea', color: '#3f7d4e', border: '#b6ddbf' },
  erp: { label: 'ERP', background: '#fbf1dd', color: '#9a6a1f', border: '#e8cf9f' },
  opensearch: { label: 'OrderX AI', background: '#e2f4f1', color: '#1f7a72', border: '#aedcd4' },
};

const CustomerSourceChip = ({ source }) => {
  const style = CUSTOMER_SOURCE_CHIP_STYLE[source];
  if (!style) return null;
  return (
    <span
      className="small"
      style={{
        flexShrink: 0,
        padding: '1px 7px',
        borderRadius: 5,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.02em',
        background: style.background,
        color: style.color,
        border: `1px solid ${style.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {style.label}
    </span>
  );
};

// Customer / Ship-To / Contact (+ Bill-To lookup) picker — shared by
// StepCustomerShipping (Step 3, where these fields may be required depending
// on checkoutFlowConfig) and StepIntake (Step 1, where they're always
// optional). Order Type and Shipping Method stay owned by StepCustomerShipping
// since they're not relevant before a request even exists.
const CustomerShipToContactFields = ({
  formValues,
  onFormValuesChange,
  onCustomerSelected,
  customerLabel = 'Customer',
  customerRequired = false,
  shipToVisible = true,
  shipToLabel = 'Ship To',
  shipToRequired = false,
  contactVisible = true,
  contactLabel = 'Contact',
  contactRequired = false,
  erpConnector = null,
}) => {
  const connector = String(erpConnector || '').toLowerCase();
  // Until settings load we don't know which ERP API to call, so do nothing.
  const connectorReady = connector === 'p21' || connector === 'eclipse';
  const isEclipse = connector === 'eclipse';
  const showContacts = contactVisible;

  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const customerTimerRef = useRef(null);
  const customerSearchSeqRef = useRef(0);

  const [shipToOptions, setShipToOptions] = useState([]);
  const [shipToLoading, setShipToLoading] = useState(false);
  const [billToLoading, setBillToLoading] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [customerLanes, setCustomerLanes] = useState(null);

  const loadCustomers = useCallback(async (query) => {
    const seq = ++customerSearchSeqRef.current;
    setCustomerLoading(true);
    try {
      const resp = await lookupOrderGenieCustomersHybrid(query);
      if (seq !== customerSearchSeqRef.current) return;
      setCustomerOptions(Array.isArray(resp?.items) ? resp.items : []);
      setCustomerLanes(resp?.lanes || null);
    } catch (err) {
      if (seq !== customerSearchSeqRef.current) return;
      setCustomerOptions([]);
      setCustomerLanes(null);
    } finally {
      if (seq === customerSearchSeqRef.current) {
        setCustomerLoading(false);
      }
    }
  }, []);

  // 300ms, not the old 2000ms: the ERP lane used to gate every keystroke on
  // its own round trip. Now ERP and OpenSearch (customer_directory) run in
  // parallel server-side and OpenSearch alone is fast enough for real
  // typeahead, so the debounce only needs to absorb normal typing cadence.
  const searchCustomersDebounced = useCallback((query) => {
    if (customerTimerRef.current) clearTimeout(customerTimerRef.current);
    const trimmed = String(query || '').trim();
    if (trimmed.length < 2) {
      customerSearchSeqRef.current += 1;
      setCustomerOptions([]);
      setCustomerLoading(false);
      setCustomerLanes(null);
      return;
    }
    setCustomerLoading(true);
    customerTimerRef.current = setTimeout(() => loadCustomers(trimmed), 300);
  }, [loadCustomers]);

  const [shipToLanes, setShipToLanes] = useState(null);

  const loadShipTo = useCallback(async (customerId, query = '') => {
    const cid = String(customerId || '').trim();
    if (!cid) {
      setShipToOptions([]);
      setShipToLoading(false);
      setShipToLanes(null);
      return;
    }
    setShipToLoading(true);
    try {
      const resp = await lookupOrderGenieShipToHybrid(cid, query);
      const items = Array.isArray(resp?.items) ? resp.items : [];
      if (items.length) setShipToOptions(items);
      setShipToLanes(resp?.lanes || null);
    } catch (err) {
      // Keep any restored ship-to seed so refresh still has an address.
    } finally {
      setShipToLoading(false);
    }
  }, []);

  const loadBillTo = useCallback(async (customerId) => {
    const cid = String(customerId || '').trim();
    if (!cid) {
      onFormValuesChange({ bill_to_details: null });
      setBillToLoading(false);
      return;
    }
    setBillToLoading(true);
    try {
      const details = await getOrderGenieErpCustomerById(cid);
      onFormValuesChange({ bill_to_details: details || null });
    } catch (err) {
      onFormValuesChange({ bill_to_details: null });
    } finally {
      setBillToLoading(false);
    }
  }, [onFormValuesChange]);

  const loadContacts = useCallback(async (customerId) => {
    const cid = String(customerId || '').trim();
    if (!cid) {
      setContactOptions([]);
      setContactLoading(false);
      return;
    }
    setContactLoading(true);
    try {
      const resp = await listOrderGenieErpContacts(cid);
      const items = Array.isArray(resp?.items) ? resp.items : [];
      setContactOptions(items);
      if (items.length === 1) {
        onFormValuesChange({
          contact: items[0].contactId,
          contact_details: items[0],
        });
      }
    } catch (err) {
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }, [onFormValuesChange]);

  const applyEclipseLookup = useCallback((lookedUp, preferredShipToId = '', preferredContactId = '') => {
    const customer = lookedUp?.customer || null;
    const cid = String(customer?.erpCustomerId || '').trim();
    const shipItems = Array.isArray(lookedUp?.ship_to_items) ? lookedUp.ship_to_items : [];
    const billDetails = lookedUp?.bill_to_details || null;
    const preferredId = String(preferredShipToId || '').trim();
    const firstShip = (preferredId && shipItems.find((s) => s.shipToId === preferredId))
      || shipItems[0]
      || null;
    // Eclipse returns contacts (contactIdList) in the same shipto response.
    const contacts = Array.isArray(lookedUp?.contacts) ? lookedUp.contacts : [];
    const contactId = String(preferredContactId || '').trim();
    const selectedContact = (contactId && contacts.find((c) => c.contactId === contactId))
      || (contacts.length === 1 ? contacts[0] : null);
    setCustomerOptions(customer ? [customer] : []);
    setShipToOptions(shipItems);
    setContactOptions(contacts);
    onFormValuesChange({
      customer: cid,
      // Keep the raw Entity ID in the input so a re-search sends the ID, not the label.
      customer_search: cid,
      customer_details: customer,
      bill_to_details: billDetails,
      ship_to: firstShip?.shipToId || '',
      ship_to_search: firstShip ? (formatShipToLabel(firstShip) || firstShip.shipToId) : '',
      ship_to_details: firstShip,
      contact: selectedContact?.contactId || '',
      contact_details: selectedContact,
    });
    if (cid && typeof onCustomerSelected === 'function') {
      onCustomerSelected(cid);
    }
  }, [onCustomerSelected, onFormValuesChange]);

  const handleEclipseLookup = useCallback(async () => {
    const entityId = String(formValues.customer || '').trim();
    if (!entityId) {
      setLookupError('Enter an Entity ID to search.');
      return;
    }
    setLookupError('');
    setCustomerLoading(true);
    setBillToLoading(true);
    setShipToLoading(true);
    try {
      const lookedUp = await lookupOrderGenieErpCustomerByEntityId(entityId);
      if (!lookedUp?.customer?.erpCustomerId) {
        setLookupError(lookedUp?.error || 'No customer found for that Entity ID.');
        setCustomerOptions([]);
        setShipToOptions([]);
        setContactOptions([]);
        onFormValuesChange({
          customer: entityId,
          customer_details: null,
          bill_to_details: null,
          ship_to: '',
          ship_to_search: '',
          ship_to_details: null,
          contact: '',
          contact_details: null,
        });
        return;
      }
      applyEclipseLookup(lookedUp);
    } catch (err) {
      setLookupError(err?.message || 'Customer lookup failed.');
      setCustomerOptions([]);
      setShipToOptions([]);
      setContactOptions([]);
    } finally {
      setCustomerLoading(false);
      setBillToLoading(false);
      setShipToLoading(false);
    }
  }, [applyEclipseLookup, formValues.customer, onFormValuesChange]);

  const handleCustomerSelect = useCallback((value, option) => {
    // `value` backfills the full "ID — Name" display text into the input
    // (see the AutoComplete options below) -- the real ERP id travels
    // alongside it on `option.erpCustomerId` so selection still resolves
    // correctly regardless of what's shown on screen.
    const cid = String(option?.erpCustomerId ?? value ?? '').trim();
    const selectedCustomer = customerOptions.find((c) => c.erpCustomerId === cid) || null;
    onFormValuesChange({
      customer: cid,
      customer_search: formatCustomerLabel(selectedCustomer) || cid,
      customer_details: selectedCustomer,
      ship_to: '',
      ship_to_search: '',
      ship_to_details: null,
      bill_to_details: null,
      contact: '',
      contact_details: null,
    });
    setContactOptions([]);
    loadShipTo(cid);
    loadBillTo(cid);
    loadContacts(cid);
    if (typeof onCustomerSelected === 'function') {
      onCustomerSelected(cid);
    }
  }, [customerOptions, loadBillTo, loadContacts, loadShipTo, onCustomerSelected, onFormValuesChange]);

  useEffect(() => () => {
    if (customerTimerRef.current) clearTimeout(customerTimerRef.current);
  }, []);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (!connectorReady || restoredRef.current) return;
    restoredRef.current = true;
    const cid = String(formValues.customer || '').trim();
    if (!cid) return;
    if (formValues.customer_details?.erpCustomerId === cid) {
      setCustomerOptions((prev) => {
        if (prev.some((item) => item.erpCustomerId === cid)) return prev;
        return [formValues.customer_details, ...prev];
      });
    } else if (!isEclipse) {
      loadCustomers(cid);
    }
    if (formValues.ship_to_details?.shipToId) {
      setShipToOptions([formValues.ship_to_details]);
    }
    if (!isEclipse) {
      loadShipTo(cid);
      if (!formValues.bill_to_details) loadBillTo(cid);
      loadContacts(cid);
    } else {
      // Eclipse has one endpoint for customer + bill-to + ship-to + contacts;
      // re-run it, keeping the previously selected ship-to / contact when
      // they are still returned.
      setBillToLoading(true);
      setShipToLoading(true);
      setContactLoading(true);
      lookupOrderGenieErpCustomerByEntityId(cid)
        .then((lookedUp) => {
          if (lookedUp?.customer?.erpCustomerId) {
            applyEclipseLookup(lookedUp, formValues.ship_to, formValues.contact);
          }
        })
        .catch(() => { /* keep restored values */ })
        .finally(() => {
          setBillToLoading(false);
          setShipToLoading(false);
          setContactLoading(false);
        });
    }
    // Restore related ERP records once, as soon as the connector is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectorReady]);

  useEffect(() => {
    const cid = String(formValues.customer || '').trim();
    if (!cid) return;
    if (formValues.customer_details?.erpCustomerId === cid) {
      const label = formatCustomerLabel(formValues.customer_details);
      const search = String(formValues.customer_search || '').trim();
      if (label && (search === cid || !search)) {
        onFormValuesChange({
          customer_details: formValues.customer_details,
          customer_search: label,
        });
      }
      return;
    }
    const matched = customerOptions.find((item) => item.erpCustomerId === cid);
    if (matched) {
      onFormValuesChange({
        customer_details: matched,
        customer_search: formatCustomerLabel(matched),
      });
    }
  }, [
    customerOptions,
    formValues.customer,
    formValues.customer_details,
    formValues.customer_search,
    onFormValuesChange,
  ]);

  useEffect(() => {
    const shipToId = String(formValues.ship_to || '').trim();
    if (!shipToId || !shipToOptions.length) return;
    const matched = shipToOptions.find((item) => item.shipToId === shipToId);
    if (!matched) return;
    const current = formValues.ship_to_details || {};
    const sameId = String(current.shipToId || '').trim() === shipToId;
    const needsSync = !sameId
      || (!current.ShipToContactFirstName && matched.ShipToContactFirstName)
      || (!current.shipToAddress && matched.shipToAddress);
    const search = String(formValues.ship_to_search || '').trim();
    const merged = { ...current, ...matched };
    const label = formatShipToLabel(merged);
    const needsLabel = Boolean(label) && (search === shipToId || !search);
    if (!needsSync && !needsLabel) return;
    onFormValuesChange({
      ship_to_details: merged,
      ...(needsLabel ? { ship_to_search: label } : {}),
    });
  }, [
    formValues.ship_to,
    formValues.ship_to_details,
    formValues.ship_to_search,
    onFormValuesChange,
    shipToOptions,
  ]);

  const matchedCustomer = customerOptions.find((c) => c.erpCustomerId === String(formValues.customer || '').trim())
    || (formValues.customer_details?.erpCustomerId === String(formValues.customer || '').trim()
      ? formValues.customer_details
      : null);
  const matchedShipTo = shipToOptions.find((s) => s.shipToId === String(formValues.ship_to || '').trim())
    || (formValues.ship_to_details?.shipToId === String(formValues.ship_to || '').trim()
      ? formValues.ship_to_details
      : null);
  const customerInputValue = (() => {
    const search = String(formValues.customer_search ?? '').trim();
    const id = String(formValues.customer || '').trim();
    if (matchedCustomer && (!search || search === id)) {
      return formatCustomerLabel(matchedCustomer);
    }
    return formValues.customer_search ?? formValues.customer ?? '';
  })();
  const shipToInputValue = (() => {
    const search = String(formValues.ship_to_search ?? '').trim();
    const id = String(formValues.ship_to || '').trim();
    if (matchedShipTo && (!search || search === id)) {
      return formatShipToLabel(matchedShipTo);
    }
    return formValues.ship_to_search ?? formValues.ship_to ?? '';
  })();
  const billToAddress = formValues.bill_to_details?.billingAddress
    || formValues.bill_to_details?.companyName
    || '';

  return (
    <>
      <div className="mt-3">
        <label className="form-label mb-1">
          {isEclipse ? 'Entity ID' : customerLabel}{customerRequired ? ' *' : ''}
        </label>
        {!connectorReady ? (
          <Input disabled placeholder="Loading ERP settings…" suffix={<Spin size="small" />} />
        ) : isEclipse ? (
          <>
            <div className="d-flex gap-2">
              <Input
                style={{ flex: 1 }}
                value={String(formValues.customer ?? '')}
                placeholder="Enter Entity ID (e.g. 48386)"
                onChange={(e) => {
                  const text = e.target.value;
                  setLookupError('');
                  onFormValuesChange({
                    customer_search: text,
                    customer: text,
                    customer_details: null,
                    ship_to: '',
                    ship_to_search: '',
                    ship_to_details: null,
                    bill_to_details: null,
                    contact: '',
                    contact_details: null,
                  });
                  setShipToOptions([]);
                  setContactOptions([]);
                }}
                onPressEnter={handleEclipseLookup}
              />
              <Button
                type="primary"
                loading={customerLoading}
                onClick={handleEclipseLookup}
              >
                Search
              </Button>
            </div>
            {lookupError && (
              <div className="small text-danger mt-1">{lookupError}</div>
            )}
            {matchedCustomer?.customerName && (
              <div className="small mt-1"><strong>{matchedCustomer.customerName}</strong></div>
            )}
          </>
        ) : (
          <AutoComplete
            style={{ width: '100%' }}
            value={customerInputValue}
            backfill
            filterOption={false}
            virtual={false}
            popupMatchSelectWidth
            notFoundContent={
              customerLoading
                ? <Spin size="small" />
                : String(formValues.customer_search || '').trim().length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No matching customers'
            }
            options={customerOptions.map((c) => {
              const cid = c.erpCustomerId || `__unlinked__:${c.customerDirectoryId}`;
              return {
                // Full "ID — Name" text so arrow-key backfill shows the
                // same complete info as a mouse click/Enter would, not
                // just the bare id. The real id for selection logic lives
                // on `erpCustomerId` below (see handleCustomerSelect).
                value: formatCustomerLabel(c) || cid,
                erpCustomerId: cid,
                disabled: !c.erpCustomerId,
                label: (
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div>
                        <span className="text-muted small me-1">ERP ID</span>
                        <strong>{c.erpCustomerId || '—'}</strong> {c.customerName}
                      </div>
                      <div className="text-muted small">
                        {c.billingAddress || (!c.erpCustomerId ? 'Indexed only — no ERP ID yet' : '')}
                      </div>
                    </div>
                    <CustomerSourceChip source={c.source} />
                  </div>
                ),
              };
            })}
            onSearch={(text) => {
              onFormValuesChange({
                customer_search: text,
                customer: text,
                customer_details: null,
                ship_to: '',
                ship_to_search: '',
                ship_to_details: null,
                bill_to_details: null,
                contact: '',
                contact_details: null,
              });
              setShipToOptions([]);
              setContactOptions([]);
              searchCustomersDebounced(text);
            }}
            onSelect={handleCustomerSelect}
          >
            <Input placeholder="Search by ERP customer ID, name, or billing address" />
          </AutoComplete>
        )}
        {!isEclipse && connectorReady && customerLanes && (customerLanes.erp === 'error' || customerLanes.erp === 'not_configured') && (
          <div className="small text-muted mt-1">
            {customerLanes.erp === 'not_configured'
              ? 'ERP live search unavailable for this connector — showing OrderX indexed results only.'
              : 'ERP search timed out — showing OrderX indexed results only.'}
          </div>
        )}
        {!isEclipse && connectorReady && customerLanes?.opensearch === 'error' && (
          <div className="small text-danger mt-1">
            OrderX indexed search failed — only live ERP results (if any) are shown. Try again, or
            check that OpenSearch is reachable for this account.
          </div>
        )}
        {matchedCustomer?.billingAddress && (
          <div className="small text-muted mt-1">{matchedCustomer.billingAddress}</div>
        )}
        {(billToLoading || billToAddress) && (
          <div className="small text-muted mt-1">
            {billToLoading ? <Spin size="small" /> : `Bill To: ${billToAddress}`}
          </div>
        )}
      </div>

      {shipToVisible && (
        <div className="mt-3">
          <label className="form-label mb-1">
            {shipToLabel}{shipToRequired ? ' *' : ''}
          </label>
          <AutoComplete
            style={{ width: '100%' }}
            value={shipToInputValue}
            backfill
            filterOption={(inputValue, option) => {
              const q = String(inputValue || '').trim().toLowerCase();
              if (!q) return true;
              const shipToId = String(option?.shipToId || '').toLowerCase();
              const matched = shipToOptions.find((s) => s.shipToId === option?.shipToId);
              const address = String(matched?.address || '').toLowerCase();
              const company = String(matched?.shipToAddress?.ShipToCompanyName || '').toLowerCase();
              return shipToId.includes(q) || address.includes(q) || company.includes(q);
            }}
            virtual={false}
            popupMatchSelectWidth
            disabled={!String(formValues.customer || '').trim() || (isEclipse && !shipToOptions.length && !matchedCustomer)}
            notFoundContent={
              shipToLoading
                ? <Spin size="small" />
                : !String(formValues.customer || '').trim()
                  ? (isEclipse ? 'Look up an Entity ID first' : 'Select a customer first')
                  : 'No ship-to addresses'
            }
            options={shipToOptions.map((s) => {
              const sid = s.shipToId || `__unlinked__:${s.addressDirectoryId}`;
              return {
                // Same full-text-backfill approach as the customer field
                // above -- arrow-key navigation should show "ID — Company",
                // not just the bare id.
                value: formatShipToLabel(s) || sid,
                shipToId: sid,
                disabled: !s.shipToId,
                label: (
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div>
                        <span className="text-muted small me-1">Ship-To ID</span>
                        <strong>{s.shipToId || '—'}</strong>
                        {s.shipToAddress?.ShipToCompanyName
                          ? ` ${s.shipToAddress.ShipToCompanyName}`
                          : ''}
                      </div>
                      <div className="text-muted small">
                        {s.address || (!s.shipToId ? 'Indexed only — no ERP ship-to ID yet' : '')}
                      </div>
                    </div>
                    <CustomerSourceChip source={s.source} />
                  </div>
                ),
              };
            })}
            onSearch={(text) => onFormValuesChange({
              ship_to_search: text,
              ship_to: text,
              ship_to_details: null,
            })}
            onSelect={(value, option) => {
              const shipToId = String(option?.shipToId ?? value ?? '').trim();
              const selected = shipToOptions.find((s) => s.shipToId === shipToId) || null;
              onFormValuesChange({
                ship_to: shipToId,
                ship_to_search: formatShipToLabel(selected) || shipToId,
                ship_to_details: selected,
              });
            }}
          >
            <Input
              placeholder={
                String(formValues.customer || '').trim()
                  ? (isEclipse ? 'Select ship-to from lookup results' : 'Select or filter ship-to')
                  : (isEclipse ? 'Look up an Entity ID first' : 'Select a customer first')
              }
            />
          </AutoComplete>
          {matchedShipTo?.address && (
            <div className="small text-muted mt-1">{matchedShipTo.address}</div>
          )}
          {!isEclipse && shipToLanes?.erp === 'error' && (
            <div className="small text-muted mt-1">
              ERP ship-to list timed out — showing OrderX indexed addresses only.
            </div>
          )}
          {!isEclipse && shipToLanes?.opensearch === 'error' && (
            <div className="small text-danger mt-1">
              OrderX indexed ship-to search failed — only live ERP addresses (if any) are shown.
            </div>
          )}
        </div>
      )}

      {showContacts && (
        <div className="mt-3">
          <label className="form-label mb-1">
            {contactLabel}{contactRequired ? ' *' : ''}
          </label>
          <Select
            style={{ width: '100%' }}
            value={formValues.contact || undefined}
            placeholder={
              String(formValues.customer || '').trim()
                ? (contactLoading ? 'Loading contacts…' : 'Select a contact')
                : 'Select a customer first'
            }
            disabled={!String(formValues.customer || '').trim()}
            loading={contactLoading}
            allowClear
            showSearch
            optionFilterProp="label"
            notFoundContent={
              contactLoading
                ? <Spin size="small" />
                : !String(formValues.customer || '').trim()
                  ? 'Select a customer first'
                  : 'No contacts'
            }
            options={contactOptions.map((c) => ({
              value: c.contactId,
              label: c.label || (
                c.contactName
                  ? `${c.contactName} (Contact ID ${c.contactId})`
                  : `Contact ID ${c.contactId}`
              ),
            }))}
            onChange={(value) => {
              const selected = contactOptions.find((c) => c.contactId === value) || null;
              onFormValuesChange({
                contact: value || '',
                contact_details: selected,
              });
            }}
          />
        </div>
      )}
    </>
  );
};

export default CustomerShipToContactFields;
